import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl, categories } from '@/mocks/categories';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://litbuyspreadsheets.click';

export default function ShoppingGuidePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main>
      {/* BreadcrumbList Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
              { '@type': 'ListItem', position: 2, name: 'Shopping Guide', item: `${siteUrl}/shopping-guide` },
            ],
          }),
        }}
      />

      {/* Article Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'Your LitBuy Spreadsheet Shopping Guide',
            description: 'Learn how to combine the power of a litbuy spreadsheet with smart shopping at Kaspreadsheet. Step-by-step guide covering category navigation, purchase logging, comparison, and tracking.',
            datePublished: '2026-06-20',
            dateModified: '2026-06-20',
            author: { '@type': 'Organization', name: 'LitBuy Spreadsheet Hub' },
            mainEntityOfPage: { '@type': 'WebPage', '@id': `${siteUrl}/shopping-guide` },
          }),
        }}
      />

      <Navbar />

      <section className="pt-28 pb-10 md:pt-36 md:pb-14 bg-background-50">
        <div className="max-w-5xl mx-auto px-4 md:px-8 text-center">
          <span className="inline-block text-xs font-label font-semibold bg-accent-100 text-accent-800 px-3 py-1.5 rounded-full mb-4">
            Shopping Guide
          </span>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-950 leading-tight">
            Your LitBuy Spreadsheet
            <br />
            Shopping Guide
          </h1>
          <p className="mt-5 text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
            Learn how to combine the power of a litbuy spreadsheet with smart shopping at Kaspreadsheet. 
            This guide walks you through everything from category navigation to maximizing your savings.
          </p>
        </div>
      </section>

      <section className="py-12 bg-background-50">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="rounded-2xl overflow-hidden mb-12">
            <img
              src="https://readdy.ai/api/search-image?query=Stylish%20fashion%20shopper%20with%20laptop%20showing%20a%20clean%20spreadsheet%20on%20screen%2C%20surrounded%20by%20beautifully%20arranged%20clothing%20items%20in%20warm%20neutral%20tones%20on%20a%20light%20oak%20table%2C%20soft%20natural%20light%20from%20window%2C%20minimal%20editorial%20scene%2C%20warm%20beige%20and%20cream%20palette&width=1200&height=500&seq=shopping-guide-hero-v2&orientation=landscape"
              alt="LitBuy Spreadsheet Shopping Guide"
              className="w-full"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                Step 1: Browse Categories
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                Start by exploring the full range of fashion categories at Kaspreadsheet. From shoes 
                and hoodies to accessories and bags, each category is organized for easy browsing. 
                Use your litbuy spreadsheet to note items that catch your eye before making decisions.
              </p>
              <div className="flex flex-wrap gap-2">
                {categories.slice(0, 6).map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-1.5 text-xs bg-background-100 text-foreground-700 px-3 py-1.5 rounded-full hover:bg-primary-100 hover:text-primary-700 transition-colors whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                Step 2: Log to Your Spreadsheet
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                As you browse, open your litbuy spreadsheet in a pinned tab. Log interesting items 
                with their name, category, price, and a quick note about why you like them. This 
                creates a personalized shopping record you can reference anytime.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                Step 3: Compare and Decide
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                Before purchasing, review your litbuy spreadsheet to compare similar items you have 
                logged. Check if you already own something similar, compare prices across categories, 
                and verify the item fits within your budget. This step prevents impulse buys.
              </p>
            </div>

            <div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                Step 4: Track After Purchase
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                After completing your order at Kaspreadsheet, update your litbuy spreadsheet with 
                the order number, purchase date, and expected delivery. Set a reminder to update 
                the status when your items arrive. This closed loop keeps your records perfect.
              </p>
            </div>
          </div>

          <div className="bg-background-100 rounded-2xl p-8 md:p-10 text-center border border-background-200/70">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
              Ready to Go Shopping?
            </h2>
            <p className="text-sm text-foreground-600 max-w-xl mx-auto mb-6 leading-relaxed">
              Open your litbuy spreadsheet, head over to Kaspreadsheet, and start browsing. 
              With this guide, you have everything you need to shop smarter and stay organized.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="px-7 py-3 bg-primary-500 text-background-50 rounded-full font-label font-semibold text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                Visit Kaspreadsheet
                <span className="w-4 h-4 inline-flex items-center justify-center ml-2">
                  <i className="ri-arrow-right-line"></i>
                </span>
              </a>
              <Link
                to="/"
                className="px-7 py-3 border-2 border-foreground-300 text-foreground-700 rounded-full font-label font-semibold text-sm hover:border-primary-500 hover:text-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}